import React, { useEffect, useState } from "react";
import { Upload, Link as LinkIcon, Save, FileText, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { BACKEND_URL } from "@/lib/api";

const STEP_LABELS = {
  proposal: "Proposal",
  budget: "Budget (Excel)",
  secretariat_form: "Secretariat Form",
  registration_school: "Registration Form (School)",
  registration_individual: "Registration Form (Individual)",
  eb_form: "EB Application Form",
  invitation_brochure: "Invitation Brochure",
  sponsorship_brochure: "Sponsorship Brochure",
};

export default function SamplesAdmin() {
  const [samples, setSamples] = useState({});
  const [feed, setFeed] = useState({ url: "", last_fetched_count: 0 });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, f] = await Promise.all([api.get("/samples"), api.get("/admin/keyword-feed")]);
    setSamples(s.data || {});
    setFeed(f.data || { url: "", last_fetched_count: 0 });
  };
  useEffect(() => { load(); }, []);

  const uploadFor = async (key, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post("/upload", fd);
    const next = { ...samples, [key]: `${BACKEND_URL}${r.data.url}` };
    setSamples(next);
    await api.put("/admin/samples", next);
    toast.success(`Sample uploaded for ${STEP_LABELS[key]}`);
  };

  const setLink = async (key, url) => {
    const next = { ...samples, [key]: url };
    setSamples(next);
  };

  const saveAll = async () => {
    await api.put("/admin/samples", samples);
    toast.success("Samples saved");
  };

  const saveFeed = async () => {
    await api.put("/admin/keyword-feed", { url: feed.url });
    toast.success("Keyword feed saved");
  };

  const testFetch = async () => {
    setBusy(true);
    try {
      const r = await api.get("/ai/keywords");
      toast.success(r.data.message);
      setFeed((f) => ({ ...f, last_fetched_count: r.data.keywords.length }));
    } catch (e) { toast.error("Fetch failed"); }
    setBusy(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display font-bold text-xl">Sample files for PMs</h3>
            <p className="text-sm text-slate-500">Upload or link a sample for each step. PMs see a "Sample" button on their workflow.</p>
          </div>
          <button onClick={saveAll} className="btn-primary" data-testid="samples-save"><Save className="w-4 h-4" /> Save links</button>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {Object.entries(STEP_LABELS).map(([k, label]) => (
            <div key={k} className="dashboard-card p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-orange-600" /><span className="font-semibold text-sm">{label}</span></div>
                {samples[k] && <a href={samples[k]} target="_blank" rel="noreferrer" className="text-xs text-orange-600 underline inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Open</a>}
              </div>
              <div className="flex gap-2">
                <input placeholder="Paste URL or upload →" className="input-pill text-xs py-2 flex-1" value={samples[k] || ""} onChange={(e) => setLink(k, e.target.value)} data-testid={`sample-${k}-url`} />
                <label className="btn-ghost py-2 px-3 text-xs cursor-pointer flex-shrink-0" title="Upload file">
                  <Upload className="w-3 h-3" />
                  <input type="file" className="hidden" onChange={(e) => uploadFor(k, e.target.files?.[0])} data-testid={`sample-${k}-file`} />
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="dashboard-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display font-bold text-lg">SEO Keyword Feed</h3>
            <p className="text-sm text-slate-500">URL returns JSON <code className="text-xs">{"{keywords:[...]}"}</code> / JSON array / CSV / one-per-line text. Content Creator auto-loads these.</p>
          </div>
          <span className="role-pill bg-indigo-100 text-indigo-700">last load: {feed.last_fetched_count}</span>
        </div>
        <div className="flex gap-2">
          <input className="input-pill text-sm flex-1" placeholder="https://your-server.com/keywords.json" value={feed.url} onChange={(e) => setFeed({ ...feed, url: e.target.value })} data-testid="keyword-feed-url" />
          <button onClick={saveFeed} className="btn-primary py-2.5 text-sm" data-testid="keyword-feed-save"><Save className="w-4 h-4" /> Save</button>
          <button onClick={testFetch} disabled={busy} className="btn-ghost py-2.5 text-sm" data-testid="keyword-feed-test"><RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Test fetch</button>
        </div>
      </div>
    </div>
  );
}
